import { useRef, useState, useCallback } from "react";
import { Move, RotateCcw, Plus } from "lucide-react";
import { Img } from "../site/Common";

/**
 * FocusPointPicker — click/drag on the image to set its object-position.
 *
 * props:
 *  - src: image url
 *  - value: {x, y} as 0..100 percentages
 *  - onChange: ({x, y}) => void
 *  - height: optional px max-height for the preview frame; defaults to 240
 *  - cropHeight: optional px height for a secondary cropped preview (shows the actual result)
 *  - testid: prefix
 */
export default function FocusPointPicker({ src, value, onChange, height = 240, cropHeight = 0, testid = "focus-picker" }) {
  const ref = useRef(null);
  const [dragging, setDragging] = useState(false);
  // Natural image aspect ratio — once we know it, we size the picker to match it so clicks
  // map directly to image pixels (no letterboxing).
  const [ratio, setRatio] = useState(null); // width / height
  const x = Math.max(0, Math.min(100, Number(value?.x ?? 50)));
  const y = Math.max(0, Math.min(100, Number(value?.y ?? 50)));

  const setFromEvent = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const cy = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    const nx = Math.round(Math.max(0, Math.min(100, (cx / rect.width) * 100)));
    const ny = Math.round(Math.max(0, Math.min(100, (cy / rect.height) * 100)));
    onChange({ x: nx, y: ny });
  }, [onChange]);

  const onImgLoad = useCallback((e) => {
    const nw = e.currentTarget?.naturalWidth || 0;
    const nh = e.currentTarget?.naturalHeight || 0;
    if (nw > 0 && nh > 0) setRatio(nw / nh);
  }, []);

  if (!src) {
    return (
      <div className="border-2 border-dashed border-stone-300 rounded-lg p-6 text-center text-xs text-stone-400 bg-stone-50" data-testid={testid}>
        Önce bir görsel yükle, sonra odak noktasını buradan ayarla.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-stone-500 flex items-center gap-1.5"><Move size={12} />Görsel üzerinde tıklayarak odak noktasını belirle</p>
        <div className="flex items-center gap-2 text-[11px] text-stone-500">
          <span className="font-mono bg-stone-100 rounded px-1.5 py-0.5" data-testid={`${testid}-coords`}>X {x}% · Y {y}%</span>
          <button type="button" onClick={() => onChange({ x: 50, y: 50 })} className="text-stone-500 hover:text-stone-800 flex items-center gap-1" data-testid={`${testid}-reset`}>
            <RotateCcw size={11} />Sıfırla
          </button>
        </div>
      </div>
      <div
        ref={ref}
        onMouseDown={(e) => { setDragging(true); setFromEvent(e); }}
        onMouseMove={(e) => { if (dragging) setFromEvent(e); }}
        onMouseUp={() => setDragging(false)}
        onMouseLeave={() => setDragging(false)}
        onTouchStart={(e) => { setDragging(true); setFromEvent(e); }}
        onTouchMove={(e) => { if (dragging) setFromEvent(e); }}
        onTouchEnd={() => setDragging(false)}
        className="relative w-full overflow-hidden rounded-lg border border-stone-300 bg-stone-100 cursor-crosshair select-none mx-auto"
        style={ratio ? { aspectRatio: `${ratio}`, maxHeight: `${height}px`, maxWidth: `${ratio * height}px` } : { height: `${height}px` }}
        data-testid={`${testid}-canvas`}
      >
        <Img src={src} alt="focus kaynağı" onLoad={onImgLoad} className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
        {/* crosshair + focus dot (animated, high-contrast) */}
        <div
          className="absolute pointer-events-none"
          style={{ left: `${x}%`, top: `${y}%`, transform: "translate(-50%, -50%)" }}
          data-testid={`${testid}-dot`}
        >
          {/* Outer pulse ring */}
          <span className="absolute inset-0 -m-1 w-9 h-9 rounded-full bg-amber-400/40 animate-ping" aria-hidden />
          {/* Main dot with crosshair icon */}
          <div className="relative w-7 h-7 rounded-full bg-amber-400 shadow-[0_0_0_3px_rgba(0,0,0,0.65),0_0_0_4px_rgba(255,255,255,0.9)] flex items-center justify-center">
            <Plus size={14} className="text-stone-900" strokeWidth={3} />
          </div>
          {/* Crosshair lines */}
          <div className="absolute left-1/2 -translate-x-1/2 -top-24 h-24 w-px bg-gradient-to-t from-amber-300 to-transparent" aria-hidden />
          <div className="absolute left-1/2 -translate-x-1/2 top-full h-24 w-px bg-gradient-to-b from-amber-300 to-transparent" aria-hidden />
          <div className="absolute top-1/2 -translate-y-1/2 -left-24 w-24 h-px bg-gradient-to-l from-amber-300 to-transparent" aria-hidden />
          <div className="absolute top-1/2 -translate-y-1/2 left-full w-24 h-px bg-gradient-to-r from-amber-300 to-transparent" aria-hidden />
        </div>
      </div>
      {cropHeight > 0 && src && (
        <div>
          <p className="text-[11px] text-stone-500 mb-1">Önizleme (kırpıldığında neyin göründüğü):</p>
          <div className="relative rounded-lg border border-stone-200 overflow-hidden" style={{ height: `${cropHeight}px` }}>
            <Img src={src} alt="odak önizleme" className="w-full h-full object-cover" style={{ objectPosition: `${x}% ${y}%` }} />
            {/* Focal-point marker on the cropped preview — small dot shows which pixel is centered */}
            <div
              className="absolute pointer-events-none"
              style={{ left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}
              data-testid={`${testid}-crop-dot`}
            >
              <span className="absolute inset-0 -m-1 w-6 h-6 rounded-full bg-amber-300/50 animate-ping" aria-hidden />
              <div className="relative w-4 h-4 rounded-full bg-amber-400 ring-2 ring-black/70 shadow-md" />
            </div>
          </div>
        </div>
      )}
      {/* fine controls */}
      <div className="grid grid-cols-2 gap-3">
        <label className="text-[11px] text-stone-500 flex items-center gap-2">
          <span className="w-5">X</span>
          <input type="range" min="0" max="100" step="1" value={x} onChange={(e) => onChange({ x: Number(e.target.value), y })} className="w-full" data-testid={`${testid}-x`} />
          <span className="w-10 text-right font-mono">{x}%</span>
        </label>
        <label className="text-[11px] text-stone-500 flex items-center gap-2">
          <span className="w-5">Y</span>
          <input type="range" min="0" max="100" step="1" value={y} onChange={(e) => onChange({ x, y: Number(e.target.value) })} className="w-full" data-testid={`${testid}-y`} />
          <span className="w-10 text-right font-mono">{y}%</span>
        </label>
      </div>
    </div>
  );
}
